import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '../../utils/cn';
import { useAssetPreviewStore } from '../../store/assetPreviewStore';
import { useAssetUrl } from '../../hooks/useAssetUrl';
import { useProjectPanelStore } from '../../store/projectPanelStore';
import { formatFileSize } from '../../../core/types/asset.types';
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Repeat,
} from 'lucide-react';

interface VideoPreviewProps {
  instanceId: string;
}

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export const VideoPreview: React.FC<VideoPreviewProps> = ({ instanceId }) => {
  const { previews } = useAssetPreviewStore();
  const { assets } = useProjectPanelStore();

  const instance = previews[instanceId];
  const asset = instance ? assets[instance.assetId] : null;

  const { url, loading, error } = useAssetUrl(instance?.assetPath || null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isLooping, setIsLooping] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [videoSize, setVideoSize] = useState<{ width: number; height: number } | null>(null);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  }, []);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      if (videoRef.current) videoRef.current.muted = !prev;
      return !prev;
    });
  }, []);

  const toggleLoop = useCallback(() => {
    setIsLooping((prev) => {
      if (videoRef.current) videoRef.current.loop = !prev;
      return !prev;
    });
  }, []);

  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    if (videoRef.current) videoRef.current.volume = v;
  }, []);

  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = x / rect.width;
    if (videoRef.current && duration > 0) {
      videoRef.current.currentTime = percent * duration;
    }
  }, [duration]);

  const handlePlaybackRateChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const rate = parseFloat(e.target.value);
    setPlaybackRate(rate);
    if (videoRef.current) videoRef.current.playbackRate = rate;
  }, []);

  const handleFullscreen = useCallback(() => {
    videoRef.current?.requestFullscreen?.();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTimeUpdate = () => setCurrentTime(video.currentTime);
    const onDurationChange = () => setDuration(video.duration);
    const onLoadedMetadata = () => {
      setDuration(video.duration);
      setVideoSize({ width: video.videoWidth, height: video.videoHeight });
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('durationchange', onDurationChange);
    video.addEventListener('loadedmetadata', onLoadedMetadata);

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('durationchange', onDurationChange);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
  }, [url]);

  if (!instance || !asset) {
    return (
      <div className="flex-1 flex items-center justify-center text-zinc-500">
        Asset not found
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="w-32 h-24 rounded bg-zinc-700 animate-pulse mb-4" />
        <div className="text-zinc-500 text-sm">Loading video...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center p-4">
        <div className="text-4xl mb-2">⚠️</div>
        <div className="text-zinc-400 text-sm">Failed to load video</div>
        <div className="text-zinc-600 text-xs mt-1">{error.message}</div>
      </div>
    );
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="flex flex-col h-full">
      {/* Video container */}
      <div className="flex-1 overflow-hidden bg-black flex items-center justify-center">
        {url && (
          <video
            ref={videoRef}
            src={url}
            className="max-w-full max-h-full object-contain"
            preload="metadata"
            playsInline
            onClick={togglePlay}
          />
        )}
      </div>

      {/* Progress bar */}
      <div
        className="h-1.5 bg-zinc-700 cursor-pointer flex-shrink-0"
        onClick={handleProgressClick}
      >
        <div
          className="h-full bg-blue-500 transition-all duration-75"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Controls bar */}
      <div className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800/80 border-t border-zinc-700 flex-shrink-0">
        <button
          className="p-1 rounded hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors"
          onClick={togglePlay}
          title={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? <Pause size={16} /> : <Play size={16} />}
        </button>

        <span className="text-xs text-zinc-400 min-w-[80px]">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>

        <div className="flex-1" />

        {/* Volume */}
        <button
          className="p-1 rounded hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors"
          onClick={toggleMute}
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted || volume === 0 ? <VolumeX size={14} /> : <Volume2 size={14} />}
        </button>
        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={isMuted ? 0 : volume}
          onChange={handleVolumeChange}
          className="w-16 h-1 bg-zinc-700 rounded-full appearance-none cursor-pointer
                     [&::-webkit-slider-thumb]:appearance-none
                     [&::-webkit-slider-thumb]:w-2.5
                     [&::-webkit-slider-thumb]:h-2.5
                     [&::-webkit-slider-thumb]:rounded-full
                     [&::-webkit-slider-thumb]:bg-white
                     [&::-webkit-slider-thumb]:cursor-pointer"
        />

        {/* Loop */}
        <button
          className={cn(
            'p-1 rounded transition-colors',
            isLooping ? 'bg-blue-600/50 text-blue-400' : 'text-zinc-500 hover:bg-zinc-700 hover:text-white'
          )}
          onClick={toggleLoop}
          title={isLooping ? 'Loop: On' : 'Loop: Off'}
        >
          <Repeat size={14} />
        </button>

        {/* Playback speed */}
        <select
          value={playbackRate}
          onChange={handlePlaybackRateChange}
          className="bg-zinc-700 text-zinc-300 text-[11px] border border-zinc-600 rounded px-1.5 py-0.5 cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500"
          title="Playback speed"
        >
          {[0.25, 0.5, 0.75, 1, 1.25, 1.5, 2].map((rate) => (
            <option key={rate} value={rate}>
              {rate}x
            </option>
          ))}
        </select>

        {/* Fullscreen */}
        <button
          className="p-1 rounded hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors"
          onClick={handleFullscreen}
          title="Fullscreen"
        >
          <Maximize size={14} />
        </button>
      </div>

      {/* Metadata footer */}
      <div className="flex items-center gap-4 px-3 py-1.5 border-t border-zinc-700 bg-zinc-800/50 text-xs text-zinc-400 flex-shrink-0">
        {videoSize && (
          <>
            <span>{videoSize.width} x {videoSize.height} px</span>
            <span className="text-zinc-600">|</span>
          </>
        )}
        <span>{formatFileSize(asset.metadata?.fileSize || 0)}</span>
        <span className="text-zinc-600">|</span>
        <span className="uppercase">{asset.extension.slice(1)}</span>
      </div>
    </div>
  );
};
